/**
 * Subscription Tier Definitions
 * 
 * This file defines all subscription tiers and their features.
 * Update this file to add new features or modify existing ones.
 */

export type SubscriptionTier = "plus" | "pro" | "elite";

export interface SubscriptionFeatures {
  scansPerWeek: number | "unlimited";
  // Accuracy levels
  accuracyLevel: "standard" | "high" | "derm-grade"; // ~90% | ~95-97% | ~98%+
  // Analysis features
  advancedAcneAnalysis: boolean; // Advanced analysis (Pro+)
  normalAcneAnalysis: boolean; // Normal analysis (Plus+)
  skinConditionFocus: "acne-oily" | "expanded" | "comprehensive"; // Plus | Pro | Elite
  // Ingredient & product features
  ingredientSafetyChecks: "basic" | "advanced" | "comprehensive"; // Basic | Advanced | Most advanced
  productDetection: "basic" | "priority" | "expert"; // Basic | Priority AI | Expert support
  // DIY Masks
  diyMaskLibraryFull: boolean; // Full library access
  diyMaskCount: number | "unlimited"; // Number of masks available
  // Diet & Food features
  customDietPlan: "none" | "advanced" | "comprehensive"; // No personalized plan | Advanced | Comprehensive
  foodScannerAtHome: boolean; // Food scanner at home access (basic for Plus)
  restaurantDatabaseAccess: boolean; // Restaurant + grocery scanning (Pro+)
  foodScannerDailyLimit: number | "unlimited"; // Daily limit for food scans
  // Progress & Insights
  progressTracker: "basic" | "weekly" | "medical"; // Basic insights | Weekly insights | Medical reports
  habitTracking: "simple" | "full" | "coaching"; // Simple reminders | Full tracking | Accountability coaching
  weeklyInsights: boolean; // Weekly improvement insights (Pro+)
  // AI Features
  aiAssistant: "basic" | "priority" | "vip-24-7"; // Basic Q&A | Priority AI | 24/7 VIP coach
  aiRoutineBuilder: "none" | "standard" | "expert"; // None | Standard | Expert support
  // Support & Reports
  exportReports: boolean; // Exportable reports (Elite only)
  premiumSupport: boolean; // Premium support
  // Elite-only features
  teleDermatology?: boolean;
  prescriptionReferrals?: boolean;
  prescriptionIngredientWarnings?: boolean;
  dedicatedSkinCoach?: boolean;
  personalizedGoals?: boolean; // Personalized goals + accountability (Elite)
}

export const GLOWLENS_FEATURES: Record<SubscriptionTier, SubscriptionFeatures> = {
  plus: {
    scansPerWeek: "unlimited", // Unlimited skin scans
    accuracyLevel: "standard", // ~90% accuracy
    advancedAcneAnalysis: false,
    normalAcneAnalysis: true, // Standard accuracy analysis
    skinConditionFocus: "acne-oily", // Acne + oily skin focus only
    ingredientSafetyChecks: "basic", // Basic ingredient safety checks
    productDetection: "basic", // Basic product recommendations
    diyMaskLibraryFull: false, // Limited library
    diyMaskCount: 10, // Limited DIY masks
    customDietPlan: "none", // No personalized diet plan - only basic food guidance
    foodScannerAtHome: true, // Food scanner at home - unlimited scans
    restaurantDatabaseAccess: false, // No restaurant access
    foodScannerDailyLimit: "unlimited", // Unlimited food scans at home
    progressTracker: "basic", // Basic progress insights (Glow progress tracking)
    habitTracking: "simple", // Simple reminders
    weeklyInsights: false,
    aiAssistant: "basic", // Basic AI Q&A assistant
    aiRoutineBuilder: "none",
    exportReports: false,
    premiumSupport: false, // Priority support (not premium, but priority level)
    prescriptionIngredientWarnings: true, // Prescription-aware guidance & warnings (all plans)
  },

  pro: {
    scansPerWeek: "unlimited", // Unlimited scans
    accuracyLevel: "high", // ~95-97% accuracy
    advancedAcneAnalysis: true, // High accuracy analysis
    normalAcneAnalysis: true,
    skinConditionFocus: "expanded", // Expanded skin conditions (dryness, sensitivity included)
    ingredientSafetyChecks: "advanced", // Advanced ingredient checks
    productDetection: "priority", // Priority AI support + product detection
    diyMaskLibraryFull: true, // Full library
    diyMaskCount: "unlimited", // Unlimited DIY mask recommendations
    customDietPlan: "advanced", // Personalized diet plan based on scans
    foodScannerAtHome: true, // Full food library + "Show more foods"
    restaurantDatabaseAccess: true, // Restaurant food scanner + cuisine filters
    foodScannerDailyLimit: "unlimited", // Unlimited food scans
    progressTracker: "weekly", // Weekly improvement insights
    habitTracking: "full", // Full habit tracking (sleep/water/stress)
    weeklyInsights: true, // Weekly improvement insights
    aiAssistant: "priority", // Priority AI support
    aiRoutineBuilder: "standard", // Routine builder
    exportReports: false,
    premiumSupport: true, // Premium support
    teleDermatology: false, // Elite only
    prescriptionIngredientWarnings: true, // Prescription-aware guidance & warnings (all plans)
  },

  elite: {
    scansPerWeek: "unlimited", // Unlimited scans
    accuracyLevel: "derm-grade", // ~98%+ accuracy + optional human review
    advancedAcneAnalysis: true, // Derm-grade accuracy
    normalAcneAnalysis: true,
    skinConditionFocus: "comprehensive", // All major skin issues: acne, oil, dryness, sensitivity, eczema, rosacea, hyperpigmentation, aging
    ingredientSafetyChecks: "comprehensive", // Most comprehensive ingredient checking
    productDetection: "expert", // Expert product support
    diyMaskLibraryFull: true,
    diyMaskCount: "unlimited", // Unlimited
    customDietPlan: "comprehensive", // Comprehensive diet guidance
    foodScannerAtHome: true,
    restaurantDatabaseAccess: true,
    foodScannerDailyLimit: "unlimited",
    progressTracker: "medical", // Exportable medical-style progress reports
    habitTracking: "coaching", // Personalized goals + accountability coaching
    weeklyInsights: true,
    aiAssistant: "vip-24-7", // 24/7 VIP AI skin coach
    aiRoutineBuilder: "expert", // Routine builder with direct expert support
    exportReports: true, // Exportable medical-style progress reports
    premiumSupport: true, // VIP support
    // Elite-only perks
    teleDermatology: true, // Tele-dermatology consults (1x/month)
    prescriptionReferrals: true,
    prescriptionIngredientWarnings: true, // Prescription-aware guidance & warnings
    dedicatedSkinCoach: true, // 24/7 VIP AI skin coach
    personalizedGoals: true, // Personalized goals + accountability coaching
  },
};

/**
 * Check if a subscription tier has a specific feature
 */
export const hasFeature = (
  tier: SubscriptionTier | undefined | null,
  feature: keyof SubscriptionFeatures
): boolean => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  const featureValue = GLOWLENS_FEATURES[validTier][feature];
  
  // Boolean features
  if (featureValue === true) return true;
  if (featureValue === false) return false;
  
  // Unlimited/number features
  if (featureValue === "unlimited") return true;
  if (typeof featureValue === 'number' && featureValue > 0) return true;
  
  // String-based features - check if it's not "none" or "basic"
  if (typeof featureValue === 'string') {
    // For features that use "none" as the lowest level
    if (featureValue === "none") return false;
    // For features that use "basic" as the lowest level, still return true (Plus has basic features)
    if (featureValue === "basic") return true;
    // All other string values (advanced, comprehensive, etc.) return true
    return true;
  }
  
  return false;
};

/**
 * Get the number of DIY masks available for a tier
 */
export const getDIYMaskCount = (tier: SubscriptionTier | undefined | null): number | "unlimited" => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  const count = GLOWLENS_FEATURES[validTier].diyMaskCount;
  return count === "unlimited" ? "unlimited" : (typeof count === 'number' ? count : 0);
};

/**
 * Get the daily food scanner limit for a tier
 */
export const getFoodScannerDailyLimit = (tier: SubscriptionTier | undefined | null): number | "unlimited" => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  return GLOWLENS_FEATURES[validTier].foodScannerDailyLimit || 0;
};

/**
 * Get the number of scans allowed per week for a tier
 */
export const getScansPerWeek = (tier: SubscriptionTier | undefined | null): number | "unlimited" => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  return GLOWLENS_FEATURES[validTier].scansPerWeek;
};

/**
 * Check if user has unlimited scans
 */
export const hasUnlimitedScans = (tier: SubscriptionTier | undefined | null): boolean => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  return GLOWLENS_FEATURES[validTier].scansPerWeek === "unlimited";
};

/**
 * Get feature description for upgrade prompts
 */
export const getUpgradeMessage = (fromTier: SubscriptionTier, toTier: SubscriptionTier): string => {
  const messages: Record<string, string> = {
    "plus->pro": "Get full AI personalization: habits, weekly insights, GlowScore™ analytics, and exportable reports.",
    "pro->elite": "Go beyond AI — get real dermatology support, prescription guidance, and elite-level skin optimization.",
  };

  const key = `${fromTier}->${toTier}`;
  return messages[key] || `Upgrade to ${toTier} to unlock more features.`;
};

/**
 * Get tier display name
 */
export const getTierDisplayName = (tier: SubscriptionTier | undefined | null): string => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  const names: Record<SubscriptionTier, string> = {
    plus: "GlowLens Plus",
    pro: "GlowLens Pro",
    elite: "GlowLens Elite",
  };
  return names[validTier];
};

/**
 * Get tier color for UI
 */
export const getTierColor = (tier: SubscriptionTier | undefined | null): string => {
  // Default to 'plus' if tier is invalid or undefined
  const validTier: SubscriptionTier = (tier && tier in GLOWLENS_FEATURES) ? tier : 'plus';
  const colors: Record<SubscriptionTier, string> = {
    plus: "#8B5CF6", // purple
    pro: "#3B82F6", // blue
    elite: "#F59E0B", // amber/gold
  };
  return colors[validTier];
};

